import { Add, CloseOutlined, Remove } from "@mui/icons-material";
import {
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  CircularProgress,
} from "@mui/material";
import { memo, useEffect, useState } from "react";

import useService from "../../../../../../hooks/useService";
import { SingleSelectDB } from "../../../utils/store";

import ActiveFilterIndicator from "./ActiveFilterIndicator";

const SingleSelect = ({
  apiUrl,
  currentValue,
  fieldName,
  isActive,
  nameKey,
  orderingVal,
  title,
  updateFilter,
  valueKey,
  fetchFromAPI = true,
  staticData: staticOptions = [],
  defaultValue = null,
  hideClearBtn = false,
}) => {
  const enumRepository = useService("repositories/enum-repository");
  const [showForm, setShowForm] = useState(false);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleOnRadioBtnChange = async (newValue) => {
    updateFilter(fieldName, newValue || null);
    const valueToStore = options.find((opt) => String(opt[valueKey]) === newValue) || null;
    await SingleSelectDB.save(fieldName, valueToStore);
  };

  async function getOptions() {
    setIsLoading(true);
    try {
      if (fetchFromAPI) {
        const limit = 100;
        const queryInitializer = String(apiUrl).endsWith("?") ? "" : "?";
        const response = await enumRepository.query({
          urlPath: `${apiUrl}${queryInitializer}limit=${limit}&ordering=${orderingVal ?? "name"}`,
        });

        setOptions(response);
      } else {
        setOptions(staticOptions);
      }
    } catch (error) {
      console.error("Error fetching options:", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (showForm) {
      getOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  return (
    <div className="relative w-full bg-white">
      <div className="flex items-center">
        <Button
          onClick={() => setShowForm(!showForm)}
          className="justify-between w-full p-0 mb-2 text-left normal-case"
        >
          <div className="flex flex-row items-baseline">
            <span className="pr-2 text-black">{title}</span>
            {isActive && <ActiveFilterIndicator />}
          </div>
          {!showForm ? <Add className="text-sky-600" /> : <Remove className="text-sky-600" />}
        </Button>
      </div>

      {/* Loading component */}
      {isLoading && showForm && (
        <div className="flex p-12 place-content-center">
          <CircularProgress color="primary" />
        </div>
      )}

      {!isLoading && showForm && (
        <div className="flex flex-wrap items-start px-4">
          {!!currentValue && !hideClearBtn && (
            <div className="flex items-center justify-end w-full mt-2">
              <Button
                onClick={() => handleOnRadioBtnChange(null)}
                variant="text"
                className="p-0 text-sm text-red-600 normal-case h-fit w-fit hover:bg-transparent hover:text-red-400"
              >
                Clear Filter <CloseOutlined className="ml-2" />
              </Button>
            </div>
          )}
          {!options || options.length === 0 ? (
            <div className="p-4">No Selections Available</div>
          ) : (
            <FormControl className="w-full my-2">
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                className="text-sm"
                defaultValue={defaultValue !== null ? defaultValue : null}
                name="radio-buttons-group"
                value={currentValue ?? defaultValue ?? null}
                onChange={(e) => handleOnRadioBtnChange(e.target.value)}
              >
                {options.map((option, idx) => (
                  <FormControlLabel
                    key={idx}
                    value={String(option[valueKey])}
                    control={<Radio />}
                    label={option[nameKey]}
                    className="text-sm"
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(SingleSelect);
